/**
 * Tareas del cierre en segundo plano (estimar, calcular, emitir).
 *
 * El backend responde 202 con la tarea y la corre en un hilo; acá se consulta
 * su avance cada 1,5 s mientras está en curso. El operador puede cerrar el
 * diálogo, seguir trabajando o salir: al volver al cierre la ve igual.
 */
import { useQuery, type QueryClient } from "@tanstack/react-query";

import { obtenerTareaActual, type TareaCierre, type TipoTareaCierre } from "./api";

/** Cada cuánto se consulta el avance mientras la tarea corre. */
export const INTERVALO_AVANCE_MS = 1500;

export const claveTarea = (cierreId: number) => ["cierre-tarea", cierreId] as const;

export function useTareaCierre(cierreId: number) {
  return useQuery({
    queryKey: claveTarea(cierreId),
    queryFn: () => obtenerTareaActual(cierreId),
    enabled: Number.isFinite(cierreId),
    refetchInterval: (q) => (q.state.data?.estado === "en_curso" ? INTERVALO_AVANCE_MS : false),
  });
}

/**
 * Tareas que esta pestaña lanzó o vio correr y cuyo final todavía no avisó.
 * Vive fuera de los componentes a propósito: si el operador sale del cierre
 * mientras corre y vuelve cuando ya terminó, igual recibe el aviso (y el
 * refresco). También cubre la tarea que termina antes del primer sondeo.
 */
const tareasPorAvisar = new Set<number>();

export function marcarPorAvisar(tareaId: number): void {
  tareasPorAvisar.add(tareaId);
}

/** true una sola vez por tarea: la que hay que avisar al terminar. */
export function tomarAviso(tareaId: number): boolean {
  return tareasPorAvisar.delete(tareaId);
}

/** Deja la tarea recién lanzada en el cache: el avance se ve al tiro. */
export function registrarTareaLanzada(qc: QueryClient, tarea: TareaCierre): void {
  marcarPorAvisar(tarea.id);
  qc.setQueryData(claveTarea(tarea.cierre_id), tarea);
}

/** Lo que cambia cuando una tarea termina: el cierre, sus lecturas y boletas. */
export function invalidarTrasTarea(qc: QueryClient, cierreId: number): void {
  for (const key of [
    ["cierre", cierreId],
    ["cierre-validaciones", cierreId],
    ["cierre-faltantes", cierreId],
    ["cierre-proforma", cierreId],
    ["cierres"],
    ["lecturas"],
    ["dte"],
  ]) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

export const enCurso = (t: TareaCierre | null | undefined): boolean =>
  t?.estado === "en_curso";

export const TITULO_TAREA: Record<TipoTareaCierre, string> = {
  estimar_faltantes: "Facturación por término medio",
  calcular: "Cálculo de boletas",
  facturar: "Emisión de boletas",
  emitir_sii: "Emisión al SII",
};

const ETAPA_POR_DEFECTO: Record<TipoTareaCierre, string> = {
  estimar_faltantes: "Estimando",
  calcular: "Calculando boletas",
  facturar: "Emitiendo boletas",
  emitir_sii: "Emitiendo al SII",
};

const n = (x: number): string => x.toLocaleString("es-CL");

/** "Emitiendo boletas… 640 de 2.025" (o "Preparando…" antes del primer avance). */
export function textoAvance(t: TareaCierre): string {
  const etapa = t.etapa ?? ETAPA_POR_DEFECTO[t.tipo];
  if (t.total <= 0) return `${etapa}…`;
  return `${etapa}… ${n(t.avance)} de ${n(t.total)}`;
}

/** 0–100, entero. Sin total todavía, 0. */
export function porcentajeAvance(t: TareaCierre): number {
  if (t.total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((t.avance / t.total) * 100)));
}

/** Resumen para el aviso al terminar bien. */
export function resumenTarea(t: TareaCierre): string {
  const r = t.resultado ?? {};
  switch (t.tipo) {
    case "estimar_faltantes": {
      const creadas = r.creadas ?? 0;
      const omitidos = r.omitidos?.length ?? 0;
      const base =
        `${n(creadas)} lectura(s) estimada(s) por ${n(r.m3_estimados ?? 0)} m³.` +
        (omitidos > 0 ? ` ${n(omitidos)} casa(s) quedan para resolver a mano.` : "");
      return `${base} Vuelve a revisar las lecturas para desbloquear el cierre.`;
    }
    case "calcular":
      return `Boletas calculadas (${n(r.boletas ?? 0)}). Revisa la vista previa antes de emitir.`;
    case "facturar":
      return `${n(r.boletas_emitidas ?? 0)} boletas emitidas.`;
    case "emitir_sii": {
      const partes = [`${n(r.emitidos ?? 0)} emitida(s) en el SII.`];
      if ((r.errores ?? 0) > 0) partes.push(`${n(r.errores ?? 0)} rechazada(s): revisa y corrige.`);
      if ((r.inciertos ?? 0) > 0)
        partes.push(`${n(r.inciertos ?? 0)} sin confirmar: no las vuelvas a emitir sin revisar.`);
      if ((r.pendientes ?? 0) > 0)
        partes.push(`${n(r.pendientes ?? 0)} pendiente(s): vuelve a intentarlo más tarde.`);
      return partes.join(" ");
    }
  }
}
